import React, { useCallback } from "react"
import LoadingAnimation from "@/common/components/loading"
import { useTranslation } from "react-i18next"
import { CartFill } from "@/skylticons"
import { toggleScroll, basketAddToGA4 } from "@/common/util"
import cn from "classnames"
import useMedia from "@/common/hooks/use_media"
import tailwindTheme from "@/common/styles/theme.module.css"
import { useMutation } from "@apollo/client"
import { graphql } from "@/api"
import { setBasketStateDoc } from "@/menu/components/basket_modal"

const basketAddDoc = graphql(`
  mutation OrderItemBasketAdd($orderItemId: Int!) {
    orderItemBasketAdd(orderItemId: $orderItemId) {
      success

      item {
        id
        priceSek
        totalCount
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

export interface AddOrderItemToCartProps {
  orderItemId: number
  classes?: string
}

const AddOrderItemToCart: React.FC<AddOrderItemToCartProps> = ({ orderItemId, classes }) => {
  const [setBasketState] = useMutation(setBasketStateDoc)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensXl})`)
  const { t } = useTranslation()

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })

    if (!desktop) {
      toggleScroll(true)
    }
  }, [setBasketState, desktop])

  const [addMutation, addMutationResult] = useMutation(basketAddDoc, {
    update: (store, { data: orderItemBasketAddData }) => {
      if (orderItemBasketAddData?.orderItemBasketAdd?.success) {
        const orderItem = orderItemBasketAddData.orderItemBasketAdd.item
        basketAddToGA4(orderItem.id, orderItem.priceSek, orderItem.totalCount)
        openBasket()
      }
    },
  })

  const addBasket = useCallback(() => {
    addMutation({ variables: { orderItemId: orderItemId } })
  }, [addMutation, orderItemId])

  const loading = addMutationResult.loading

  return (
    <button
      id="add_to_cart"
      className={cn("order-item-to-cart button button-emphasis", classes)}
      type="button"
      onClick={addBasket}
      disabled={loading}
    >
      {loading ? (
        <LoadingAnimation />
      ) : (
        <span className="flex items-center justify-center">
          <CartFill />
          {t("signomatic.add_to_basket")}
        </span>
      )}
    </button>
  )
}

export default AddOrderItemToCart
